<script setup lang="ts">
import { useUserManagementStore } from '@/stores/userManagement';
import { Table, UserAvatar } from '@/components/common';
import { TableColumnType } from '@/components/common/table/interfaces/TableColumnModel';
import { onMounted, ref } from 'vue';
import { UserModel } from '@/models/user';
import UserExpanded from './UserExpanded.vue';
import Chip from 'primevue/chip';
import { ButtonType, CustomButton } from '@/components';

const emit = defineEmits(['openInviteDialog']);

const studyUsersStore = useUserManagementStore();

const expandedRows = ref<UserModel[]>([]);

const handleRowToggle = (clickedRow: UserModel) => {
  const index = expandedRows.value.findIndex(
    row => row.identifier === clickedRow.identifier
  );

  if (index === -1) {
    expandedRows.value.push(clickedRow);
  } else {
    expandedRows.value.splice(index, 1);
  }
};

const handleCollapseRequest = (rowToCollapse: UserModel) => {
  const index = expandedRows.value.findIndex(
    row => row.identifier === rowToCollapse.identifier
  );

  if (index !== -1) {
    expandedRows.value.splice(index, 1);
  }
};

const onPerPageRowCountChanged = (perPage: number) => {
  expandedRows.value = [];
};

onMounted(async () => {
  await studyUsersStore.fetchPendingUsers();
});
</script>

<template>
  <Table :columns="studyUsersStore.tableColumns"
    :data-list="studyUsersStore.pendingUsers"
    :loading-state="studyUsersStore.isPendingUsersLoading.isLoading"
    :expandedRows="expandedRows"
    :rows="10"
    @onPerPageRowCountChanged="onPerPageRowCountChanged"
    @onRowClick="handleRowToggle">
    <template #bodyContent="{ data, col }">
      <UserAvatar :avatarUrl="data.avatar"
        :userName="data[col.key]"
        :user-email="data.email"
        :size="44"
        v-if="col.type === TableColumnType.USER"/>

      <div v-if="
          col.key === 'permissionsList' &&
          !expandedRows.some((row) => row.identifier === data.identifier)
        "
        class="permissions-container">
        <span v-for="(permission, index) in data.permissionsList" :key="index">
          <Chip :label="permission" />
        </span>
      </div>

      <div v-else-if="
          col.type === TableColumnType.TEXT &&
          !expandedRows.some((row) => row.identifier === data.identifier)
        ">
        {{ data[col.key] }}
      </div>
    </template>
    <template #emptyContent>
      <div class="empty-content-container">
        <div class="empty-content">
          <FontAwesomeIcon :icon="[`fal`, 'mailbox']" class="icon" />
          <h3 class="no-entries-label">No users invited yet</h3>
          <div class="create-context-wrapper">
            <div class="new-entry-text">To invite a user click on</div>
            <CustomButton text="Invite users"
                  :type="ButtonType.POSITIVE"
                  :marginLeft="16"
                  @onClick="emit('openInviteDialog')"/>
          </div>
        </div>
      </div>
    </template>
    <template #expandContent="{ data }">
      <UserExpanded :user="data" @onRowCollapse="handleCollapseRequest(data)" />
    </template>
  </Table>
</template>

<style scoped lang="scss">
:deep(.p-row-even) {
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }
}
:deep(.p-row-odd) {
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }
}

.permissions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
}
.create-context-wrapper {
  display: flex;
  align-items: center;
  margin: 16px 0;
}
.empty-content-container {
  .empty-content {
    .icon {
      font-size: 100px;
      margin: 16px 0px;
    }
  }
}
</style>
